import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { translations, Language } from '@/lib/translations/index';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language, fromProfile?: boolean) => void;
  t: (key: string, paramsOrFallback?: Record<string, string | number> | string) => string;
}

const defaultLanguage: Language = 'en';

const defaultT = (key: string, paramsOrFallback?: Record<string, string | number> | string): string => {
  const keys = key.split('.');
  let value: any = translations[defaultLanguage];

  for (const k of keys) {
    value = value?.[k];
  }

  // Handle fallback string
  if (!value) {
    if (typeof paramsOrFallback === 'string') {
      return paramsOrFallback;
    }
    return key;
  }

  // Handle interpolation params
  if (paramsOrFallback && typeof paramsOrFallback === 'object' && typeof value === 'string') {
    return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
      return paramsOrFallback[paramKey]?.toString() ?? `{{${paramKey}}}`;
    });
  }

  return value;
};

const defaultContext: LanguageContextType = {
  language: defaultLanguage,
  setLanguage: () => {},
  t: defaultT,
};

const LanguageContext = createContext<LanguageContextType>(defaultContext);

const LANGUAGE_STORAGE_KEY = 'jci-language';
const LANGUAGE_CHANGED_KEY = 'jci-language-user-changed';

const getStoredLanguage = (): Language => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && ['en', 'pt', 'es', 'fr', 'ja'].includes(stored)) {
      return stored as Language;
    }
  } catch (e) {
    console.error('Error reading language setting:', e);
  }
  return 'en';
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getStoredLanguage);
  const userChangedRef = useRef(false);

  const setLanguage = useCallback((lang: Language, fromProfile = false) => {
    // If this is coming from profile sync but user recently changed it manually, ignore
    if (fromProfile) {
      const recentlyChanged = localStorage.getItem(LANGUAGE_CHANGED_KEY);
      if (recentlyChanged) {
        const changedAt = parseInt(recentlyChanged, 10);
        // Ignore profile sync for 5 seconds after user change
        if (Date.now() - changedAt < 5000) {
          return;
        }
      }
    }
    
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    
    // Mark as user-changed if not from profile
    if (!fromProfile) {
      localStorage.setItem(LANGUAGE_CHANGED_KEY, Date.now().toString());
    }
  }, []);

  const t = (key: string, paramsOrFallback?: Record<string, string | number> | string): string => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    // Fallback to English if translation not found
    if (!value) {
      let fallback: any = translations['en'];
      for (const k of keys) {
        fallback = fallback?.[k];
      }
      value = fallback;
    }
    
    // If still no value, use provided fallback string or key
    if (!value) {
      if (typeof paramsOrFallback === 'string') {
        return paramsOrFallback;
      }
      return key;
    }
    
    // Handle interpolation: replace {{key}} with params[key]
    if (paramsOrFallback && typeof paramsOrFallback === 'object' && typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
        return paramsOrFallback[paramKey]?.toString() ?? `{{${paramKey}}}`;
      });
    }
    
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation(): LanguageContextType {
  return useContext(LanguageContext);
}